import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiWithAuth } from '../../lib/api';
import toast from 'react-hot-toast';
import {
  ChevronLeftIcon,
  MapIcon,
  GlobeAltIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';

const LocationSettings = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  
  const [settings, setSettings] = useState({
    location_enabled: false,
    current_location: null,
    search_radius: 10,
    auto_search: false,
    background_location: false
  });
  const [loading, setLoading] = useState(true);
  const [locationStatus, setLocationStatus] = useState('unknown'); // 'granted', 'denied', 'prompt', 'unknown'
  
  // 住所編集用の新しい状態
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [addressForm, setAddressForm] = useState({
    prefecture: '',
    city: '',
    address: ''
  });
  const [addressLoading, setAddressLoading] = useState(false);

  useEffect(() => {
    fetchLocationSettings();
    checkLocationPermission();
    loadUserAddress();
  }, []);

  const loadUserAddress = () => {
    if (user) {
      setAddressForm({
        prefecture: user.prefecture || '',
        city: user.city || '',
        address: user.address || ''
      });
    }
  };

  const fetchLocationSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const authApi = apiWithAuth(token);
      const res = await authApi.get('/users/settings/location');
      if (res && res.data) {
        setSettings(prev => ({ ...prev, ...res.data }));
      }
    } catch (error) {
      console.error('Failed to fetch location settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkLocationPermission = async () => {
    if ('permissions' in navigator) {
      try {
        const permission = await navigator.permissions.query({ name: 'geolocation' });
        setLocationStatus(permission.state);
        
        permission.addEventListener('change', () => {
          setLocationStatus(permission.state);
        });
      } catch (error) {
        console.error('Permission query failed:', error);
      }
    }
  };

  const saveAddress = async () => {
    try {
      setAddressLoading(true);
      const token = localStorage.getItem('token');
      const authApi = apiWithAuth(token);
      // Prefer legacy path in production, fallback to newer alias
      let res = await authApi.put('/user/profile', {
        prefecture: addressForm.prefecture,
        city: addressForm.city,
        address: addressForm.address,
      });
      if (!(res && res.status >= 200 && res.status < 300)) {
        res = await authApi.put('/users/profile', {
          prefecture: addressForm.prefecture,
          city: addressForm.city,
          address: addressForm.address,
        });
      }
      const { data: updatedUser } = res;
        
        // AuthContextのユーザー情報を更新
        updateUser(updatedUser);
        
        toast.success('住所を更新しました');
        setIsEditingAddress(false);
        
        // 住所から緯度経度を取得（逆ジオコーディング）
        if (addressForm.prefecture && addressForm.city) {
          await geocodeAddress();
        }
      
    } catch (error) {
      console.error('Error saving address:', error);
      toast.error('住所の保存でエラーが発生しました');
    } finally {
      setAddressLoading(false);
    }
  };

  const geocodeAddress = async () => {
    try {
      const fullAddress = `${addressForm.prefecture}${addressForm.city}${addressForm.address}`;
      const token = localStorage.getItem('token');
      const authApi = apiWithAuth(token);
      const { data } = await authApi.get(`/location/geocode`, { params: { address: fullAddress } });
      if (data && data.latitude && data.longitude) {
          const newLocation = {
            latitude: data.latitude,
            longitude: data.longitude,
            address: fullAddress
          };
          
          setSettings(prev => ({
            ...prev,
            current_location: newLocation
          }));
      }
    } catch (error) {
      console.error('Error geocoding address:', error);
    }
  };

  const handleAddressChange = (field, value) => {
    setAddressForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const requestLocationPermission = async () => {
    if ('geolocation' in navigator) {
      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 300000
          });
        });

        // 逆ジオコーディングして「都道府県 + 市区町村（番地は除外）」の日本語表記を取得
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        const displayAddress = await reverseGeocodeJaNoHouse(lat, lng);

        const newLocation = {
          latitude: lat,
          longitude: lng,
          address: displayAddress || '現在地'
        };

        setSettings(prev => ({
          ...prev,
          location_enabled: true,
          current_location: newLocation
        }));

        setLocationStatus('granted');
        toast.success('位置情報の取得を許可しました');
        
        // APIに設定を保存（実装待ち）
        await saveSettings({
          ...settings,
          location_enabled: true,
          current_location: newLocation
        });

      } catch (error) {
        console.error('Location permission denied:', error);
        setLocationStatus('denied');
        toast.error('位置情報の取得が拒否されました');
      }
    } else {
      toast.error('このブラウザは位置情報をサポートしていません');
    }
  };

  // Nominatim の逆ジオコーディングで日本語住所を取得し、番地などは省略
  const reverseGeocodeJaNoHouse = async (lat, lng) => {
    try {
      const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lng)}&accept-language=ja&zoom=15&addressdetails=1`;
      const res = await fetch(url, {
        headers: {
          'Accept': 'application/json'
        }
      });
      if (!res.ok) return '';
      const data = await res.json();
      const a = data.address || {};
      // 都道府県相当
      const prefecture = a.state || a.province || '';
      // 市区町村相当（city/town/village/municipality）
      const municipality = a.city || a.town || a.village || a.municipality || a.locality || '';
      // 区など（city_district / ward）
      const ward = a.city_district || a.ward || a.county || '';
      // 町名（suburb / neighbourhood / quarter など）
      const town = a.suburb || a.neighbourhood || a.neighborhood || a.quarter || '';
      // 丁目番地などは含めない（road, house_number などは無視）
      // 表示は「都道府県 + 市区町村 + 区 + 町」まで
      const parts = [prefecture, municipality, ward, town].filter(Boolean);
      return parts.join('');
    } catch (e) {
      console.warn('reverseGeocode failed:', e);
      return '';
    }
  };

  const saveSettings = async (newSettings) => {
    try {
      const token = localStorage.getItem('token');
      const authApi = apiWithAuth(token);
      await authApi.put('/users/settings/location', newSettings);
      setSettings(newSettings);
      toast.success('設定を保存しました');
    } catch (error) {
      toast.error('設定の保存に失敗しました');
    }
  };

  const handleToggle = (key) => {
    if (key === 'location_enabled' && !settings.location_enabled) {
      requestLocationPermission();
    } else {
      const newSettings = {
        ...settings,
        [key]: !settings[key]
      };
      saveSettings(newSettings);
    }
  };

  const handleRadiusChange = (radius) => {
    const newSettings = {
      ...settings,
      search_radius: radius
    };
    saveSettings(newSettings);
  };

  const getLocationStatusIcon = () => {
    switch (locationStatus) {
      case 'granted':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'denied':
        return <XCircleIcon className="w-5 h-5 text-red-500" />;
      default:
        return <InformationCircleIcon className="w-5 h-5 text-yellow-500" />;
    }
  };

  const getLocationStatusText = () => {
    switch (locationStatus) {
      case 'granted':
        return '許可済み';
      case 'denied':
        return '拒否されています';
      case 'prompt':
        return '許可が必要です';
      default:
        return '不明';
    }
  };

  const radiusOptions = [1, 3, 5, 10, 20, 50];

  // 表示用: 日本語住所から番地・丁目などの細かい要素を可能な範囲で省く簡易フォーマッタ
  const formatAddressJaNoHouseText = (addrText) => {
    if (!addrText) return '';
    // 「丁目」「番」「番地」「号」以降をおおまかに削除（正規表現ベースの簡易処理）
    // 例: 東京都渋谷区神南1丁目23-4 → 東京都渋谷区神南
    const patterns = [/\d+丁目.*/, /\d+番.*/, /\d+番地.*/, /\d+号.*/];
    let out = addrText;
    for (const p of patterns) {
      out = out.replace(p, '');
    }
    return out.trim();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">読み込み中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <button
              onClick={() => navigate('/mypage')}
              className="mr-4 p-2 text-gray-600 hover:text-gray-900"
            >
              <ChevronLeftIcon className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">位置情報の設定</h1>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 現在の位置情報状態 */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center mb-4">
            <MapIcon className="w-6 h-6 mr-3 text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900">位置情報の状態</h2>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-gray-700">ブラウザの許可状態</span>
              </div>
              <div className="flex items-center">
                {getLocationStatusIcon()}
                <span className="ml-2 text-sm text-gray-600">{getLocationStatusText()}</span>
              </div>
            </div>

            {settings.current_location && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">現在地</div>
                <div className="text-gray-900">{formatAddressJaNoHouseText(settings.current_location.address)}</div>
                <div className="text-xs text-gray-500 mt-1">
                  緯度: {settings.current_location.latitude.toFixed(6)}, 
                  経度: {settings.current_location.longitude.toFixed(6)}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 住所編集セクション */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <MapIcon className="w-6 h-6 mr-3 text-gray-400" />
              <h2 className="text-lg font-semibold text-gray-900">希望勤務地の設定</h2>
            </div>
            {!isEditingAddress && (
              <button
                onClick={() => setIsEditingAddress(true)}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                編集
              </button>
            )}
          </div>

          {isEditingAddress ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    都道府県
                  </label>
                  <select
                    value={addressForm.prefecture}
                    onChange={(e) => handleAddressChange('prefecture', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">選択してください</option>
                    <option value="北海道">北海道</option>
                    <option value="青森県">青森県</option>
                    <option value="岩手県">岩手県</option>
                    <option value="宮城県">宮城県</option>
                    <option value="秋田県">秋田県</option>
                    <option value="山形県">山形県</option>
                    <option value="福島県">福島県</option>
                    <option value="茨城県">茨城県</option>
                    <option value="栃木県">栃木県</option>
                    <option value="群馬県">群馬県</option>
                    <option value="埼玉県">埼玉県</option>
                    <option value="千葉県">千葉県</option>
                    <option value="東京都">東京都</option>
                    <option value="神奈川県">神奈川県</option>
                    <option value="新潟県">新潟県</option>
                    <option value="富山県">富山県</option>
                    <option value="石川県">石川県</option>
                    <option value="福井県">福井県</option>
                    <option value="山梨県">山梨県</option>
                    <option value="長野県">長野県</option>
                    <option value="岐阜県">岐阜県</option>
                    <option value="静岡県">静岡県</option>
                    <option value="愛知県">愛知県</option>
                    <option value="三重県">三重県</option>
                    <option value="滋賀県">滋賀県</option>
                    <option value="京都府">京都府</option>
                    <option value="大阪府">大阪府</option>
                    <option value="兵庫県">兵庫県</option>
                    <option value="奈良県">奈良県</option>
                    <option value="和歌山県">和歌山県</option>
                    <option value="鳥取県">鳥取県</option>
                    <option value="島根県">島根県</option>
                    <option value="岡山県">岡山県</option>
                    <option value="広島県">広島県</option>
                    <option value="山口県">山口県</option>
                    <option value="徳島県">徳島県</option>
                    <option value="香川県">香川県</option>
                    <option value="愛媛県">愛媛県</option>
                    <option value="高知県">高知県</option>
                    <option value="福岡県">福岡県</option>
                    <option value="佐賀県">佐賀県</option>
                    <option value="長崎県">長崎県</option>
                    <option value="熊本県">熊本県</option>
                    <option value="大分県">大分県</option>
                    <option value="宮崎県">宮崎県</option>
                    <option value="鹿児島県">鹿児島県</option>
                    <option value="沖縄県">沖縄県</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    市区町村
                  </label>
                  <input
                    type="text"
                    value={addressForm.city}
                    onChange={(e) => handleAddressChange('city', e.target.value)}
                    placeholder="新宿区"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  町名・番地（任意）
                </label>
                <input
                  type="text"
                  value={addressForm.address}
                  onChange={(e) => handleAddressChange('address', e.target.value)}
                  placeholder="歌舞伎町1-1-1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={saveAddress}
                  disabled={addressLoading || !addressForm.prefecture || !addressForm.city}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {addressLoading ? '保存中...' : '保存'}
                </button>
                <button
                  onClick={() => {
                    setIsEditingAddress(false);
                    loadUserAddress();
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                >
                  キャンセル
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-sm text-gray-600 mb-1">現在の設定</div>
              <div className="text-gray-900">
                {addressForm.prefecture && addressForm.city 
                  ? `${addressForm.prefecture}${addressForm.city}${addressForm.address ? ` ${addressForm.address}` : ''}`
                  : '住所が設定されていません'
                }
              </div>
              <div className="text-xs text-gray-500 mt-2">
                この住所を基準にして周辺の求人を表示します
              </div>
            </div>
          )}
        </div>

        {/* 基本設定 */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">基本設定</h2>
          
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="font-medium text-gray-900">位置情報の利用</h3>
                <p className="text-sm text-gray-600">近くの求人を見つけるために位置情報を使用します</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.location_enabled}
                  onChange={() => handleToggle('location_enabled')}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            {settings.location_enabled && (
              <>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">自動検索</h3>
                    <p className="text-sm text-gray-600">位置情報が変わった時に自動的に求人を検索します</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.auto_search}
                      onChange={() => handleToggle('auto_search')}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">バックグラウンドでの位置取得</h3>
                    <p className="text-sm text-gray-600">アプリを使用していない時も位置情報を更新します</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.background_location}
                      onChange={() => handleToggle('background_location')}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </>
            )}
          </div>
        </div>

        {/* 検索範囲設定 */}
        {settings.location_enabled && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">検索範囲</h2>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                現在地からの検索範囲: {settings.search_radius}km
              </label>
              
              <div className="grid grid-cols-3 gap-3">
                {radiusOptions.map((radius) => (
                  <button
                    key={radius}
                    onClick={() => handleRadiusChange(radius)}
                    className={`px-4 py-2 rounded-lg font-medium ${
                      settings.search_radius === radius
                        ? 'bg-blue-100 text-blue-700 border-2 border-blue-500'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-transparent'
                    }`}
                  >
                    {radius}km
                  </button>
                ))}
              </div>
              
              <p className="text-xs text-gray-500 mt-2">
                範囲が広いほどより多くの求人が見つかりますが、通勤時間も長くなります
              </p>
            </div>
          </div>
        )}

        {/* 位置情報の更新 */}
        {settings.location_enabled && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">位置情報の更新</h2>
            
            <button
              onClick={requestLocationPermission}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
            >
              <GlobeAltIcon className="w-5 h-5 mr-2" />
              現在地を再取得
            </button>
            
            <p className="text-xs text-gray-500 mt-2 text-center">
              移動した場合は現在地を再取得してください
            </p>
          </div>
        )}

        {/* プライバシーについて */}
        <div className="bg-blue-50 rounded-lg p-6">
          <div className="flex items-start">
            <InformationCircleIcon className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-900 mb-2">プライバシーについて</h4>
              <ul className="text-blue-800 text-sm space-y-1">
                <li> 位置情報は求人検索とマッチングにのみ使用されます</li>
                <li> 正確な住所は第三者に提供されません</li>
                <li> いつでも位置情報の利用を停止できます</li>
                <li> 位置情報はデバイス内で安全に処理されます</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationSettings;